import { Box, Stack, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";

import { StyledAnchor } from "@/components/common/styles";
import {
  SITE_STEPS,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Author } from "@/content/types";

const AVATAR = 80;

/** The card's frame: a pale rounded panel that darkens under the pointer, and rests on those colours on a phone. */
const CARD_FRAME = (theme: Theme) => ({
  p: 3,
  maxWidth: "100%",
  border: 1,
  borderColor: marketingPalette.surface,
  borderRadius: "16px",
  backgroundColor: marketingPalette.surface,
  // The card sets the quiet ink once and the name opts back out.
  color: marketingPalette.inkSubtle,
  [theme.breakpoints.down(SITE_STEPS.wide)]: { justifyContent: "center" },
  "&:hover": {
    backgroundColor: alpha(marketingPalette.wash, 0.83),
    borderColor: alpha(marketingPalette.ink, 0.36),
  },
  // On a phone the card stacks, and rests on the two colours it takes
  // under the pointer at every other width.
  [TINY]: {
    flexDirection: "column",
    rowGap: "16px",
    backgroundColor: alpha(marketingPalette.wash, 0.83),
    borderColor: alpha(marketingPalette.ink, 0.36),
  },
});

/** The bio card the published article closes with: a portrait beside the author's name, role and short bio. */
export default function AuthorCard({ author }: { author: Author | null }) {
  if (!author) {
    // The published template draws the card for a post with no author too,
    // empty and linking to "#": a bare frame the share row sits under.
    return (
      <Stack
        component={StyledAnchor}
        href="#"
        direction="row"
        sx={CARD_FRAME}
      />
    );
  }

  return (
    <Stack
      component={StyledAnchor}
      href={`/author/${author.slug}`}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      columnGap={4}
      sx={CARD_FRAME}
    >
      <Stack
        direction="row"
        alignItems="center"
        columnGap={4}
        sx={{
          [TINY]: {
            flexDirection: "column",
            alignItems: "flex-start",
            columnGap: 0,
            rowGap: "12px",
            fontSize: 14,
            lineHeight: "22px",
            width: "100%",
          },
        }}
      >
        {author.avatar ? (
          <Box
            component="img"
            src={author.avatar.url}
            alt=""
            loading="lazy"
            // A plain `img`, not MUI's `Avatar`, whose img slot forces
            // `object-fit: cover` and crops these portraits. The square box
            // gives way with the row, which is why the published portrait
            // reads narrower than it is tall.
            sx={{
              width: AVATAR,
              height: AVATAR,
              maxWidth: "100%",
              borderRadius: "100px",
              objectFit: "fill",
            }}
          />
        ) : null}
        <Stack
          rowGap={2}
          alignItems="flex-start"
          sx={{ [TINY]: { columnGap: "8px", width: "100%" } }}
        >
          <Stack
            direction="row"
            alignItems="center"
            columnGap={2}
            sx={{
              fontSize: 14,
              lineHeight: "21px",
              textTransform: "uppercase",
              color: alpha(marketingPalette.inkSubtle, 0.88),
              [TINY]: {
                flexDirection: "column",
                alignItems: "flex-start",
                rowGap: "4.8px",
                lineHeight: "22px",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 500,
                lineHeight: "30px",
                textTransform: "none",
                color: marketingPalette.ink,
                [TINY]: { lineHeight: "22px" },
              }}
            >
              {author.name}
            </Typography>
            {/* An empty role draws nothing, gap included, as Webflow hides
                an empty binding. */}
            {author.jobTitle ? (
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: "21px",
                  textTransform: "uppercase",
                  color: alpha(marketingPalette.inkSubtle, 0.88),
                  [TINY]: { lineHeight: "22px" },
                }}
              >
                {author.jobTitle}
              </Typography>
            ) : null}
          </Stack>
          {author.cardBio ? (
            <Typography
              sx={{
                lineHeight: "24px",
                color: marketingPalette.inkSubtle,
                [TINY]: { fontSize: 14, lineHeight: "22px" },
              }}
            >
              {author.cardBio}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
}
